import moment from 'moment';
import 'moment-timezone';
import {
  BADGES,
  COLORS,
  buildBrandedPdf,
  infoRow,
  isBlank,
  mapPaymentMode,
  titleCase,
} from '../../../components/fleetnexis/pdf/brandedPdf';

// quotations.quote_status: '0' pending, '1' converted to booking, '2' not converted
const QUOTE_STATUS = {
  0: { badge: 'AWAITING APPROVAL', text: 'Pending Approval', ...BADGES.warning },
  1: { badge: 'CONVERTED TO BOOKING', text: 'Converted to Booking', ...BADGES.success },
  2: { badge: 'NOT CONVERTED', text: 'Not Converted', ...BADGES.neutral },
};

/**
 * pdfMake document definition for a quotation.
 * lines: [{ name, subtitle?, vehicle, amount }] — amount is pre-tax.
 */
const buildQuotationPdf = ({
  quote,
  customerName,
  lines,
  timeSlot,
  vehicleType,
  preparedBy,
  logo,
  currency,
  taxLineItems,
  getAmountWithTax,
  timezone,
}) => {
  const status = QUOTE_STATUS[String(quote?.quote_status ?? '0')] || QUOTE_STATUS[0];
  const quoteNo = quote?.service_id || String(quote?.id ?? '');
  const preferredDate =
    quote?.prefered_date && moment(quote.prefered_date).isValid()
      ? moment(quote.prefered_date).format('DD MMM YYYY')
      : '-';

  // Near by follows the house number, as on the quotation form.
  const addressLines = [
    quote?.house_no,
    isBlank(quote?.near_by) ? '' : `Near by: ${String(quote.near_by).trim()}`,
    quote?.address,
    [quote?.area, quote?.city].filter((v) => !isBlank(v)).map(titleCase).join(', '),
  ].filter((v) => !isBlank(v));

  return buildBrandedPdf({
    logo,
    title: 'QUOTATION',
    badge: { text: status.badge, color: status.color, fill: status.fill },
    reference: `Quotation Ref: QTN-${quoteNo}`,
    issued: (timezone ? moment().tz(timezone) : moment()).format('DD MMM YYYY, hh:mm A'),
    customerCard: {
      title: 'QUOTATION FOR / CUSTOMER',
      rows: [
        { text: customerName || '-', fontSize: 10.5, bold: true, color: COLORS.ink, margin: [0, 0, 0, 1] },
        { text: quote?.phone || '-', fontSize: 8.5, color: COLORS.slate700 },
        ...(quote?.email ? [{ text: quote.email, fontSize: 8, color: COLORS.muted }] : []),
        ...addressLines.map((line, i) => ({
          text: line,
          fontSize: 8,
          color: COLORS.slate600,
          margin: [0, i === 0 ? 3 : 0, 0, 0],
        })),
      ],
    },
    detailsCard: {
      title: 'SERVICE & APPOINTMENT',
      rows: [
        infoRow('Quotation ID:', `#${quoteNo}`, { bold: true, color: COLORS.ink }),
        infoRow('Preferred Date:', preferredDate, { bold: true }),
        infoRow('Time Slot:', timeSlot || '-'),
        infoRow('Vehicle Type:', vehicleType),
      ],
    },
    paymentCard: {
      title: 'PAYMENT DETAILS',
      rows: [
        infoRow('Method:', mapPaymentMode(quote?.payment_mode), { width: 65, bold: true, color: COLORS.ink }),
        infoRow('Booking:', titleCase(quote?.booking_status), { width: 65 }),
        infoRow('Status:', status.text, { width: 65, bold: true, color: status.color }),
      ],
    },
    indexHeader: 'SERIAL NO.',
    middleHeader: 'VEHICLE',
    lines: lines.map((line) => ({ ...line, middle: line.vehicle })),
    emptyMessage: 'No services or spare parts have been added to this quotation.',
    currency,
    taxLineItems,
    getAmountWithTax,
    totalLabel: 'TOTAL QUOTED AMOUNT',
    signatureName: preparedBy,
    supportText: 'Questions about this quotation? Our team is always here for you.',
    footerText: 'FleetNexis Mobile Car Care   |   Computer Generated Quotation',
  });
};

export default buildQuotationPdf;
